import { createContext, useContext, useEffect, useState } from "react";

// Theme presets — each defines the CSS custom properties that skin the entire app.
// The default (Command Orange) is the original FuelTrack look and is unchanged.
// NOTE: RayFuel is designed dark-first. Every preset is dark so legacy pages
// that still use hardcoded surfaces stay readable across themes.
export const THEMES = {
  command: {
    id: "command",
    name: "Command Orange",
    hint: "The original RayFuel tactical dark",
    vars: {
      "--bg-base": "#0A0A0A",
      "--bg-surface": "#121212",
      "--bg-elevated": "#1A1A1A",
      "--bg-muted": "#141414",
      "--border-line": "#262626",
      "--text-primary": "#FFFFFF",
      "--text-secondary": "#A3A3A3",
      "--text-tertiary": "#737373",
      "--accent": "#FF5A1F",
      "--accent-hover": "#E04F1A",
      "--accent-fg": "#000000",
    },
  },
  midnight: {
    id: "midnight",
    name: "Midnight Blueprint",
    hint: "Deep blue with cyan accents",
    vars: {
      "--bg-base": "#050914",
      "--bg-surface": "#0B1220",
      "--bg-elevated": "#131C2E",
      "--bg-muted": "#0F1727",
      "--border-line": "#1E2A44",
      "--text-primary": "#FFFFFF",
      "--text-secondary": "#94A3B8",
      "--text-tertiary": "#64748B",
      "--accent": "#22D3EE",
      "--accent-hover": "#06B6D4",
      "--accent-fg": "#031B22",
    },
  },
  forest: {
    id: "forest",
    name: "Forest Sport",
    hint: "Warm greens for touring modes",
    vars: {
      "--bg-base": "#08120C",
      "--bg-surface": "#0E1B14",
      "--bg-elevated": "#152A1F",
      "--bg-muted": "#0B1912",
      "--border-line": "#1F3A2B",
      "--text-primary": "#F5F5F5",
      "--text-secondary": "#A3B8AB",
      "--text-tertiary": "#6E8677",
      "--accent": "#34D399",
      "--accent-hover": "#10B981",
      "--accent-fg": "#03150C",
    },
  },
  crimson: {
    id: "crimson",
    name: "Crimson Track",
    hint: "Motorsport red on graphite",
    vars: {
      "--bg-base": "#0B0709",
      "--bg-surface": "#150C10",
      "--bg-elevated": "#1F1218",
      "--bg-muted": "#170D12",
      "--border-line": "#2E1720",
      "--text-primary": "#FFFFFF",
      "--text-secondary": "#C5A3AC",
      "--text-tertiary": "#7A5661",
      "--accent": "#F43F5E",
      "--accent-hover": "#E11D48",
      "--accent-fg": "#0B0709",
    },
  },
};

const ThemeCtx = createContext(null);

const STORAGE_KEY = "rf_theme";

function applyTheme(theme) {
  const root = document.documentElement;
  Object.entries(theme.vars).forEach(([k, v]) => root.style.setProperty(k, v));
  root.setAttribute("data-theme", theme.id);
  // Keep body background in sync so mobile status bar matches
  document.body.style.background = theme.vars["--bg-base"];
}

export function ThemeProvider({ children }) {
  const [themeId, setThemeId] = useState(() => localStorage.getItem(STORAGE_KEY) || "command");

  useEffect(() => {
    const theme = THEMES[themeId] || THEMES.command;
    applyTheme(theme);
    localStorage.setItem(STORAGE_KEY, theme.id);
  }, [themeId]);

  return (
    <ThemeCtx.Provider value={{ themeId, setThemeId, themes: THEMES }}>
      {children}
    </ThemeCtx.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeCtx);
}
